import { Directive, ElementRef, Inject, Input, OnDestroy, OnInit, Optional, Renderer2, Self } from '@angular/core';
import { NgControl } from '@angular/forms';
import { MAT_FORM_FIELD, MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { Store } from '@ngrx/store';
import { combineLatest, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { TripData, TripState } from '../models/trip.model';
import { User } from '../models/user.model';
import { getLoggedUser } from '../store/shared/selectors-shared';
import { getTripToEdit } from '../store/trip/selectors-trip';

/**
 * This directive combines multiple readonly directives (role-based, trip-state-based) the logic is similar to the CombinedVisibilityDirective
 * but to handle readonly states.
 * 
 */
@Directive({
    selector: '[appCombinedReadonly]',
    standalone: true,
    exportAs: 'appCombinedReadonly'
})
export class CombinedReadonlyDirective implements OnInit, OnDestroy {

    @Input() allowedStates: string[] = [];
    @Input() allowedRoles: string[] = [];
    @Input() forbiddenStates: string[] = [];
    @Input() forbiddenRoles: string[] = [];
    @Input() trip: TripData | null | undefined;
    @Input() combineMode: 'AND' | 'OR' = 'OR';

    private subscription: Subscription = Subscription.EMPTY;
    private currentUser?: User;
    private currentTrip?: TripData;

    isReadonly = false;

    constructor(
        private el: ElementRef,
        private renderer: Renderer2,
        private store: Store,
        // this ngControl is to interact with reactive forms element 
        // if the directive is applied to a form control
        @Optional() @Self() private ngControl: NgControl | null,
        // matInput owns the readonly attribute through its host binding, so it must be driven via its own input
        @Optional() @Self() private matInput: MatInput | null,
        @Optional() @Inject(MAT_FORM_FIELD) private formField: MatFormField | null
    ) { }

    ngOnInit() {
        this.subscription = combineLatest([
            this.store.select(getLoggedUser),
            this.store.select(getTripToEdit)
        ]).pipe(
            map(([user, stateTrip]) => {
                this.currentUser = user;
                this.currentTrip = this.trip || stateTrip;
                this.updateReadonly();
            })
        ).subscribe();
    }

    private updateReadonly() {
        let roleCheck = true;
        let stateCheck = true;

        // Check Roles
        roleCheck = this.checkRole(roleCheck);

        // Check States
        stateCheck = this.checkState(stateCheck);

        if (this.combineMode === 'OR') {
            this.isReadonly = !roleCheck || !stateCheck;
        } else {
            // AND mode: it's readonly if BOTH conditions are met for BEING READONLY
            // Wait, the user said "if the user has role ACCOUNTANT and the trip is in state PAID_BY_CUSTOMER"
            // So if I use forbiddenRoles=['ACCOUNTANT'] and forbiddenStates=['PAID_BY_CUSTOMER'] with AND
            // It should be readonly if role is ACCOUNTANT AND state is PAID_BY_CUSTOMER.

            // Let's rethink the logic to be more intuitive for "Readonly" which is the inverse of visibility.
            // If I want "ACCOUNTANT" AND "PAID_BY_CUSTOMER" to trigger readonly:
            // I should probably have a more direct way or use the forbidden logic correctly.

            const isRoleForbidden = this.forbiddenRoles.length > 0 && !!this.currentUser?.userAttributes?.['custom:role'] && this.forbiddenRoles.includes(this.currentUser.userAttributes['custom:role']);
            const effectiveState = this.getEffectiveTripState();
            const isStateForbidden = this.forbiddenStates.length > 0 && !!effectiveState && this.forbiddenStates.includes(effectiveState);

            if (this.combineMode === 'AND') {
                this.isReadonly = isRoleForbidden && isStateForbidden;
            } else {
                this.isReadonly = isRoleForbidden || isStateForbidden;
            }
        }

        this.applyReadonlyAttribute();
        this.applyReadonlyStyle();
    }

    /**
     * A CANCELLED trip (reachable only from PAID_BY_CUSTOMER) must be handled by the readonly rules exactly as a
     * PAID_BY_CUSTOMER trip, so it is evaluated as PAID_BY_CUSTOMER.
     */
    private getEffectiveTripState(): TripState | undefined {
        const tripState = this.currentTrip?.tripState as TripState | undefined;
        return tripState === TripState.CANCELLED ? TripState.PAID_BY_CUSTOMER : tripState;
    }

    private checkState(stateCheck: boolean) {
        if (this.allowedStates.length > 0 || this.forbiddenStates.length > 0) {
            const tripState = this.getEffectiveTripState();
            if (this.allowedStates.length > 0) {
                stateCheck = !!tripState && this.allowedStates.includes(tripState);
            }
            if (this.forbiddenStates.length > 0) {
                const isForbidden = !!tripState && this.forbiddenStates.includes(tripState);
                stateCheck = this.allowedStates.length > 0 ? (stateCheck && !isForbidden) : !isForbidden;
            }
        }
        return stateCheck;
    }

    private checkRole(roleCheck: boolean) {
        if (this.allowedRoles.length > 0 || this.forbiddenRoles.length > 0) {
            const userRole = this.currentUser?.userAttributes?.['custom:role'];
            if (this.allowedRoles.length > 0) {
                roleCheck = !!userRole && this.allowedRoles.includes(userRole);
            }
            if (this.forbiddenRoles.length > 0) {
                const isForbidden = !!userRole && this.forbiddenRoles.includes(userRole);
                roleCheck = this.allowedRoles.length > 0 ? (roleCheck && !isForbidden) : !isForbidden;
            }
        }
        return roleCheck;
    }

    private applyReadonlyAttribute() {
        const nativeElement = this.el.nativeElement;
        // If we are attached to a reactive form control (NgControl) and the host is
        // not a plain INPUT/TEXTAREA, prefer enabling/disabling the control via the
        // reactive forms API. This avoids using the `disabled` attribute directly
        // in templates alongside `formControlName`, which triggers Angular warnings.
        if (this.ngControl?.control && nativeElement.tagName !== 'INPUT' && nativeElement.tagName !== 'TEXTAREA') {
            if (this.isReadonly) {
                if (!this.ngControl.control.disabled) {
                    this.ngControl.control.disable({ emitEvent: false });
                }
            } else {
                if (this.ngControl.control.disabled) {
                    this.ngControl.control.enable({ emitEvent: false });
                }
            }
            return;
        }

        // Fallback for simple input/textarea elements: use the readonly attribute
        if (nativeElement.tagName === 'INPUT' || nativeElement.tagName === 'TEXTAREA') {
            if (this.matInput) {
                this.matInput.readonly = this.isReadonly;
            }
            if (this.isReadonly) {
                this.renderer.setAttribute(nativeElement, 'readonly', 'true');
            } else {
                this.renderer.removeAttribute(nativeElement, 'readonly');
            }
        }
    }

    private applyReadonlyStyle() {
        const nativeElement = this.el.nativeElement;

        if (this.isReadonly) {
            this.renderer.addClass(nativeElement, 'mat-form-field-readonly');
        } else {
            this.renderer.removeClass(nativeElement, 'mat-form-field-readonly');
        }

        // a matInput can't style its enclosing mat-form-field from the template (the parent binding would be checked
        // before this directive updates isReadonly, raising ExpressionChangedAfterItHasBeenCheckedError), so do it here;
        // the label is styled by the .mat-form-field-readonly css rule since it's not projected yet at this point
        if (this.matInput && this.formField) {
            const formFieldElement = this.formField._elementRef.nativeElement;
            if (this.isReadonly) {
                this.renderer.addClass(formFieldElement, 'mat-form-field-readonly');
            } else {
                this.renderer.removeClass(formFieldElement, 'mat-form-field-readonly');
            }
        }
    }


    ngOnDestroy() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }
}
